import { useMutation } from '@apollo/client';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNotifications } from 'lead-list/components/notifications';
import { useStateMachine } from 'little-state-machine';
import { useAuthContext } from 'providers/auth';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Navigate, useNavigate, useParams } from 'react-router';
import routes from 'routes';
import { CreateLeadList, CreateLeadListVariables } from 'types/apollo/CreateLeadList';
import {
  GenerateLeadListSignedURL,
  GenerateLeadListSignedURLVariables,
} from 'types/apollo/GenerateLeadListSignedURL';
import { ListType } from 'types/apollo/globalTypes';
import {
  NotifyLeadListUploaded,
  NotifyLeadListUploadedVariables,
} from 'types/apollo/NotifyLeadListUploaded';
import CREATE_LEAD_LIST from '../../createLeadList.graphql';
import GEN_LEAD_LIST_SIGNED_URL from '../../generateLeadListSignedUrl.graphql';
import NOTIFY_LEAD_LIST_UPLOADED from '../../notifyLeadListUploaded.graphql';
import messages from './messages';

const joinUrl = (...parts: string[]) => parts.join('/').replace(/\/+/g, '/');

export const StepImporting: FC = () => {
  const navigate = useNavigate();
  const { id: companyIdParam } = useParams<{ id: string }>();

  const { isInternal, user } = useAuthContext();
  const { formatMessage } = useIntl();
  const {
    state: { leadListCreate },
  } = useStateMachine();
  const { notifyError, notifySuccess } = useNotifications();

  const [step, setStep] = useState<
    'starting' | 'creating' | 'getting-signed-url' | 'uploading' | 'done'
  >('starting');
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');
  const hasRun = useRef(false);

  const [createLeadList] = useMutation<CreateLeadList, CreateLeadListVariables>(CREATE_LEAD_LIST);
  const [generateSignedUrl] = useMutation<
    GenerateLeadListSignedURL,
    GenerateLeadListSignedURLVariables
  >(GEN_LEAD_LIST_SIGNED_URL);
  const [notifyLeadListUploaded] = useMutation<
    NotifyLeadListUploaded,
    NotifyLeadListUploadedVariables
  >(NOTIFY_LEAD_LIST_UPLOADED);

  const parsedCompanyId = companyIdParam !== undefined ? Number(companyIdParam) : undefined;
  const requiredCompanyIdForInternal =
    isInternal && typeof parsedCompanyId === 'number' && Number.isFinite(parsedCompanyId)
      ? parsedCompanyId
      : undefined;

  const campaignKeyToSend = useMemo(() => {
    const c = leadListCreate.campaign;
    if (!c) return undefined;
    return isInternal ? String(c.shortId) : c.shortId;
  }, [isInternal, leadListCreate.campaign]);

  const goToInternal = (childPath: string) => {
    if (requiredCompanyIdForInternal === undefined) return;
    const base = routes.company.resolve({ id: requiredCompanyIdForInternal });
    navigate(joinUrl(base, routes.company.leads.path, childPath));
  };

  const goToExternal = (childPath: string) => {
    navigate(joinUrl(routes.leads.path, childPath));
  };

  const importList = async () => {
    try {
      const hasCampaignKeyIfProspective =
        leadListCreate.listType !== ListType.PROSPECTIVE || !!campaignKeyToSend;

      const hasCompanyWhenInternal = !isInternal || requiredCompanyIdForInternal !== undefined;

      if (
        !leadListCreate.listType ||
        !hasCampaignKeyIfProspective ||
        !hasCompanyWhenInternal ||
        !leadListCreate.file?.name ||
        !leadListCreate.mappings
      ) {
        notifyError('Missing required data. Returning to start.', 'missing-data');
        navigate('..', { replace: true, relative: 'path' });
        return;
      }

      const swappedMappings: Record<string, string> = {};
      for (const [header, mappedField] of Object.entries(leadListCreate.mappings)) {
        swappedMappings[mappedField] = header;
      }

      setStep('creating');

      const createVars: CreateLeadListVariables = {
        input: {
          shortId: campaignKeyToSend,
          fileName: leadListCreate.file.name,
          estimatedTotalRows: leadListCreate.estimatedTotalRows,
          columnMapping: swappedMappings,
          listType: leadListCreate.listType,
          userName: user?.name || '',
          ...(isInternal && requiredCompanyIdForInternal !== undefined
            ? { companyId: requiredCompanyIdForInternal }
            : {}),
        },
      };

      const newLeadList = await createLeadList({ variables: createVars });
      const newLeadListId = newLeadList.data?.createLeadList?.id;

      if (!newLeadListId) {
        notifyError(formatMessage(messages.newLeadListErrorNotification), 'newLeadList-error');
        setStatus('error');
        return;
      }

      const signedUrlResult = await generateSignedUrl({
        variables: {
          id: Number(newLeadListId),
          fileName: leadListCreate.file.name,
          listType: leadListCreate.listType,
        },
      });

      const signedUrl = signedUrlResult.data?.generateLeadListSignedURL?.signedUrl;
      if (!signedUrl) {
        notifyError(
          formatMessage(messages.genLeadListSignedURLErrorNotification),
          'genLeadListSignedURL-error',
        );
        setStatus('error');
        return;
      }

      setStep('uploading');

      const uploadRes = await fetch(signedUrl, {
        method: 'PUT',
        body: leadListCreate.file,
        headers: { 'content-type': 'application/octet-stream' },
      });

      if (!uploadRes.ok) {
        notifyError('Upload to S3 failed.', 's3-upload-error');
        setStatus('error');
        return;
      }

      await notifyLeadListUploaded({ variables: { id: Number(newLeadListId) } });

      setStep('done');
      setStatus('success');
      notifySuccess(formatMessage(messages.newLeadListSuccessNotification), 'newLeadList-success');

      setTimeout(() => {
        if (isInternal) {
          goToInternal(routes.company.leads.pending.path);
        } else {
          goToExternal(routes.leads.pending.path);
        }
      }, 1500);
    } catch (error) {
      console.error('Unexpected error during import:', error);
      notifyError('An unexpected error occurred. Please try again.', 'import-error');
      setStatus('error');
    }
  };

  const handleGoToLeadListsClick = () => {
    if (isInternal) {
      goToInternal(routes.company.leads.imports.path);
    } else {
      goToExternal(routes.leads.imports.path);
    }
  };

  const handleStartOverClick = () => {
    if (isInternal) {
      goToInternal(routes.company.leads.importCreate.path);
    } else {
      goToExternal(routes.leads.importCreate.path);
    }
  };

  useEffect(() => {
    if (!hasRun.current) {
      hasRun.current = true;
      importList();
    }
  }, []);

  if (
    (leadListCreate.listType === ListType.PROSPECTIVE && !campaignKeyToSend) ||
    !leadListCreate.file?.name ||
    !leadListCreate.mappings ||
    (isInternal && requiredCompanyIdForInternal === undefined)
  ) {
    return <Navigate to=".." replace relative="path" />;
  }

  return (
    <Stack spacing={2} alignItems="center">
      <Typography variant="h4">
        {formatMessage(messages.hangTight)} — {step}
      </Typography>

      {status === 'error' && (
        <Stack spacing={1} direction="row">
          <Button variant="outlined" onClick={handleStartOverClick}>
            Start Over
          </Button>
          <Button
            variant="contained"
            color="error"
            sx={{ color: 'white' }}
            onClick={handleGoToLeadListsClick}
          >
            Go to Lead Lists
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
